import {
    fromEvent,
    Observable,
} from 'rxjs';

import {
    first,
    map,
} from 'rxjs/operators';

import {
    pipe,
    fork,
    alt,
    not,
    tap,
} from './functional';

import { isIOS } from '../helpers/helperFunctions';

export function bindFunctions(element: any, ...funcs: Array<(target: any, ...args: any[]) => void>) {
    return function (...args) {
        funcs.forEach((f, i) => f(element, args[i]));
        return element;
    }
}

export function createElement(selector: string): HTMLElement {
    return document.createElement(selector);
}

export function createAnchor(href: string = '', text: string = '', className: string = ''): HTMLAnchorElement {
    return bindFunctions(
        createElement('a'),
        addHref,
        addTextContent,
        addClassName
    )(href, text, className);
}

export function createSpan(className: string): HTMLElement {
    return addClassName(createElement('span'), className);
};

export function createDiv(className: string): HTMLElement {
    return addClassName(createElement('div'), className);
};

export function createNav(className: string): HTMLElement {
    return addClassName(createElement('nav'), className);
};

export function createUList(className: string): HTMLElement {
    return addClassName(createElement('ul'), className);
};

export function createListItem(className: string): HTMLElement {
    return addClassName(createElement('li'), className);
};

export function createButton(className: string): HTMLElement {
    return addClassName(createElement('button'), className);
};

export function createElementFromString(str: string): HTMLElement {
    const wrapper = createDiv('');
    wrapper.innerHTML = str;
    return <HTMLElement>wrapper.firstElementChild || wrapper;
}

export function createSVG(inner: string): HTMLElement {
    const svg = document.createElement('svg');
    svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    svg.setAttribute('viewBox', '0 0 86 86');
    svg.innerHTML = inner;

    return svg;
}

export function addTextContent(element: HTMLElement, value: string): HTMLElement {
    element.textContent = value;
    return element;
}

export function addInnerHTML(element: HTMLElement, value: string): HTMLElement {
    element.innerHTML = value;
    return element;
}

export function addClassName(element: HTMLElement, value: string): HTMLElement {
    element.className = value;
    return element;
}

export function addClass(element: HTMLElement, value: string): HTMLElement {
    element.classList.add(value);
    return element;
}

export function addClasses(element: HTMLElement, ...classes: string[]): HTMLElement {
    classes.forEach(cls => addClass(element, cls));
    return element;
}

export function removeClass(node: HTMLElement, className: string): HTMLElement {
    node.classList.remove(className);
    return node;
}

export function removeClasses(node: HTMLElement, ...classes: string[]): HTMLElement {
    classes.forEach(cls => removeClass(node, cls));
    return node;
}

export function addClassToList(list: Element[] | HTMLElement[] | HTMLCollection | NodeList, className: string) {
    Array.from(list).forEach(itm => addClass(<HTMLElement>itm, className));
    return list;
}

export function removeClassFromList(list: Element[] | HTMLElement[] | HTMLCollection | NodeList, className: string) {
    Array.from(list).forEach(itm => removeClass(<HTMLElement>itm, className));
    return list;
}

export function hasClass(
    element: HTMLElement | Element | EventTarget,
    value: string
): boolean {
    return (<HTMLElement>element).classList.contains(value);
}

export function addHref(element: HTMLAnchorElement, value: string): HTMLAnchorElement {
    element.href = value;
    return element;
}

export function addToElement(element: HTMLElement, ...items: HTMLElement[]): HTMLElement {
    items.filter(i => i).forEach(i => element.appendChild(i));
    return element;
}

export function addToElementAtTheBeginning(element: HTMLElement, ...items: HTMLElement[]): HTMLElement {
    const children = <HTMLElement[]>Array.from(element.children);
    return addToElement(element, ...items.concat(children));
}

export function addElementsFromArray(target: HTMLElement, arr: HTMLElement[]) {
    arr.forEach(e => addToElement(target, e));
    return arr;
}

export function getElementHeight(element: HTMLElement): number {
    return element.offsetHeight;
}

export function setElementHeight(element: HTMLElement, height: number) {
    element.style.height = height ? height + 'px' : '';
    return this;
}

export function isInViewPort(element: HTMLElement): boolean {
    const elementRect = element.getBoundingClientRect(),
        offsetHeight = window.innerHeight;

    return elementRect.top < 0 && elementRect.bottom > 0
        || elementRect.top >= 0 && elementRect.top < offsetHeight
        || elementRect.bottom > 0 && elementRect.bottom < offsetHeight;
}

export function getSibling(element: HTMLElement, selector: string): HTMLElement {
    return <HTMLElement>element.parentElement.querySelector(selector);
}

export function getClosest(element: HTMLElement, selector: string): HTMLElement {
    do {
        if (element.matches(selector)) {
            return element;
        }
        element = <HTMLElement>element.parentElement || <HTMLElement>element.parentNode;
    } while (element !== null && element.nodeType === 1);

    return null;
}

export function createScriptTag(script: HTMLScriptElement): HTMLScriptElement {
    let domScript = document.createElement('script');
    domScript.type = 'text/javascript';

    if (script.src) {
        domScript.src = script.src;
    } else {
        domScript.textContent = script.innerText;
    }

    if (script.defer) {
        domScript.defer = true;
    }

    return domScript;
}

export function insertScript(script: HTMLScriptElement): HTMLScriptElement {
    let domScript = document.createElement('script');
    domScript.type = 'text/javascript';

    if (script.src) {
        domScript.src = script.src;
    } else {
        domScript.textContent = script.innerText;
    }

    if (script.defer) {
        domScript.defer = true;
    }

    return document.head.appendChild(domScript);
}

export function injectScriptsFromAjaxResponse(element: HTMLElement): Promise<any[]> {

    const scripts = Array.from(element.getElementsByTagName('script'))
        .filter(script => !isScriptTagRendered(script))
        .map(createScriptTag);

    return Promise.all(scripts.map(script => new Promise((resolve, reject) => {
        if (script.src) {
            script.onload = () => resolve();
            script.onerror = () => {
                console.error(`Layer script was not loaded: URL ${(<HTMLScriptElement>event.target).src}`);
                resolve();
            };
        } else {
            resolve();
        }

        document.head.appendChild(script);
    })));
}

export function isScriptTagRendered(script: HTMLScriptElement): boolean {
    var exist: boolean = false;
    if (script.src) {
        (document.querySelectorAll(`[src="${script.src}"]`).length > 0 && script.src.indexOf("recaptcha") == -1) ? exist = true : exist = false;
    }

    return exist;
}

export function toggleClass(node: HTMLElement, className: string, force?: boolean): HTMLElement {
    if (typeof force === undefined) {
        node.classList.toggle(className);
    } else {
        force
            ? node.classList.add(className)
            : node.classList.remove(className);
    }

    return node;
}

export function getChildren(node: HTMLElement, selector?: string): HTMLElement[] {
    let children = <HTMLElement[]>Array.from(node.children);

    if (selector) {
        children = children.filter(ch => hasClass(ch, selector));
    }

    return children;
}

export function dispatchEvent(element: HTMLElement, event: Event): HTMLElement {
    element.dispatchEvent(event);
    return element;
}

/*
 *
 * Helper Functions for ImageLoad
 *
 */

export const listenToLoad$ = fork(arg => arg, loadPicture$, setSourceAttr);
export const addSpinnerToImage = alt(not(isImage), showSpinner);
export const loadImageLazy$ = pipe(tap(addSpinnerToImage), listenToLoad$);

export function isImage(element: HTMLElement): HTMLImageElement | false {
    return element.tagName === 'IMG' ? <HTMLImageElement>element : false;
}

export function isImageLoaded(img: HTMLImageElement): boolean {
    return (img.hasAttribute('src') || img.hasAttribute('srcset'))
        || (!isIOS && img.complete && img.naturalHeight !== 0);
}

export function hasImageToBeLoaded(img: HTMLImageElement): boolean {
    return !isImageLoaded(img) && isInViewPort(img);
}

export function loadPicture$(img: HTMLImageElement): Observable<HTMLImageElement> {
    return fromEvent(img, 'load').pipe(
        first(),
        map(e => <HTMLImageElement>e.target),
        map(removeDataAttr),
        map(hideSpinner),
        tap(() => document.dispatchEvent(new CustomEvent('Eucerin:lazyimageloaded'))));
}

export function setSourceAttr(img: HTMLImageElement): HTMLImageElement {
    const src = img.dataset.src,
        srcset = img.dataset.srcset;

    srcset && img.setAttribute('srcset', srcset);
    src && img.setAttribute('src', src);

    return img;
}

export function removeDataAttr(img: HTMLImageElement): HTMLImageElement {
    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');

    return img;
}

export function showSpinner(img: HTMLImageElement) {
    addClass(img.parentElement, 'lazy-spinner');
    return img;
}

export function hideSpinner(img: HTMLImageElement) {
    removeClass(img.parentElement, 'lazy-spinner');
    return img;
}
